import type {TalentPoolListSuggestionActions} from 'src/action-creators/talent-pools/talent-pool-suggestion';
import type {TalentPoolCandidateActions} from 'src/action-creators/talent-pools/candidate';

import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';

import type {Dispatch, ThunkAction, GetState} from 'src/reducers';
import type {
  TalentPoolList,
  TalentPoolUsersResponse,
  TalentPoolUserSearchQuery,
  TalentPoolListQuery,
  TalentPoolCreatePayload,
  TalentPoolFolder,
  TalentPoolHierarchy,
  TalentPoolWorkflowFolders,
} from 'src/types/talent-pools';
import * as React from 'react';
import {
  Toast,
  ToastTitle,
  TOAST_SEMANTIC,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/index';
import {
  TALENT_POOL_FOLDERS_PAGE_SIZE,
  TALENT_POOL_FOLDERS_HIERARCHY_PAGE_SIZE,
} from 'src/constants/talent-pools';
import {fetching, key} from 'src/utils/redux';

import {camel} from 'src/utils';

import * as reduxApi from 'src/utils/redux-api';
import {thunkify as flow} from 'src/utils/thunks';


const API_PATH: ApiOptions = {
  apiPath: '/trmapi/api/v1/',
};

const API_POST_PATH: ApiPostOptions = {
  apiPath: '/trmapi/api/v1/',
};

export const TALENT_POOL_LIST = 'talent-pools/talent-pool-list';
export const TALENT_POOL_LIST_ERROR = 'talent-pools/talent-pool-list-error';
export const TALENT_POOL_USERS_SEARCH = 'talent-pools/talent-pool-users-search';
export const TALENT_POOL_USERS_SEARCH_ERROR =
  'talent-pools/talent-pool-users-search-error';
export const TALENT_POOL_DETAILS = 'talent-pools/talent-pool-details';
export const TALENT_POOL_DETAILS_ERROR =
  'talent-pools/talent-pool-details-error';
export const TALENT_POOL_FOLDERS = 'talent-pools/talent-pool-folders';
export const TALENT_POOL_FOLDERS_ERROR =
  'talent-pools/talent-pool-folders-error';
export const TALENT_POOL_FOLDER_DETAILS =
  'talent-pools/talent-pool-folder-details';
export const TALENT_POOL_FOLDER_DETAILS_ERROR =
  'talent-pools/talent-pool-folder-details-error';
export const TALENT_POOL_FOLDERS_HIERARCHY =
  'talent-pools/talent-pool-folders-hierarchy';
export const TALENT_POOL_FOLDERS_HIERARCHY_ERROR =
  'talent-pools/talent-pool-folders-hierarchy-error';
export const TALENT_POOL_FOLDERS_WORKFLOW =
  'talent-pools/talent-pool-folders-workflow';
export const TALENT_POOL_FOLDERS_WORKFLOW_ERROR =
  'talent-pools/talent-pool-folders-workflow-error';

type TalentPoolListAction = {
  type: typeof TALENT_POOL_LIST;
  payload: TalentPoolList;
};

type TalentPoolListErrorAction = {
  type: typeof TALENT_POOL_LIST_ERROR;
  payload: Error;
};

type TalentPoolUsersSearchAction = {
  type: typeof TALENT_POOL_USERS_SEARCH;
  payload: TalentPoolUsersResponse;
};

type TalentPoolUsersSearchErrorAction = {
  type: typeof TALENT_POOL_USERS_SEARCH_ERROR;
  payload: Error;
};

type TalentPoolDetailsAction = {
  type: typeof TALENT_POOL_DETAILS;
  payload: TalentPoolFolder;
};

type TalentPoolDetailsErrorAction = {
  type: typeof TALENT_POOL_DETAILS_ERROR;
  payload: Error;
};

type TalentPoolFoldersAction = {
  type: typeof TALENT_POOL_FOLDERS;
  payload: TalentPoolList;
};

type TalentPoolFoldersErrorAction = {
  type: typeof TALENT_POOL_FOLDERS_ERROR;
  payload: Error;
};

type TalentPoolFolderDetailsAction = {
  type: typeof TALENT_POOL_FOLDER_DETAILS;
  payload: TalentPoolFolder;
};

type TalentPoolFolderDetailsErrorAction = {
  type: typeof TALENT_POOL_FOLDER_DETAILS_ERROR;
  payload: Error;
};

type TalentPoolFoldersHierarchyAction = {
  type: typeof TALENT_POOL_FOLDERS_HIERARCHY;
  payload: TalentPoolHierarchy;
};

type TalentPoolFoldersHierarchyErrorAction = {
  type: typeof TALENT_POOL_FOLDERS_HIERARCHY_ERROR;
  payload: Error;
};

type TalentPoolFoldersWorkflowAction = {
  type: typeof TALENT_POOL_FOLDERS_WORKFLOW;
  payload: TalentPoolWorkflowFolders;
};

type TalentPoolFoldersWorkflowErrorAction = {
  type: typeof TALENT_POOL_FOLDERS_WORKFLOW_ERROR;
  payload: Error;
};

export type TalentPoolActions =
  | TalentPoolListAction
  | TalentPoolListErrorAction
  | TalentPoolUsersSearchAction
  | TalentPoolUsersSearchErrorAction
  | TalentPoolDetailsAction
  | TalentPoolDetailsErrorAction
  | TalentPoolFoldersAction
  | TalentPoolFoldersErrorAction
  | TalentPoolFolderDetailsAction
  | TalentPoolFolderDetailsErrorAction
  | TalentPoolListSuggestionActions
  | TalentPoolCandidateActions
  | TalentPoolFoldersHierarchyAction
  | TalentPoolFoldersHierarchyErrorAction
  | TalentPoolFoldersWorkflowAction
  | TalentPoolFoldersWorkflowErrorAction;

export const getTalentPoolList: (
  query: TalentPoolListQuery,
) => ThunkAction<void> = flow(
  key(() => `talent-pool-folders`),
  fetching(),
)((query: TalentPoolListQuery) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get<TalentPoolList>(
      `talent-pool-folders?page_size=${query.page_size}&page=${query.page}&q=${query.query}&status=${query.status}`,
      {},
      API_PATH,
    ),
  )
    .then((response: TalentPoolList) => {
      dispatch({
        type: TALENT_POOL_LIST,
        payload: response,
      });
    })
    .catch((error: Error) => {
      dispatch({
        type: TALENT_POOL_LIST_ERROR,
        payload: error,
      });
    });
});

export const searchTalentPoolUsers: (
  query: TalentPoolUserSearchQuery,
) => ThunkAction<void> = flow(
  key(() => `talent-pool-folders/users`),
  fetching(),
)((query: TalentPoolUserSearchQuery) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get<TalentPoolUsersResponse>(
      `talent-pool-folders/users?page_size=${query.page_size}&page=${query.page}&q=${query.query}`,
      {},
      API_PATH,
    ),
  )
    .then((response: TalentPoolUsersResponse) => {
      dispatch({
        type: TALENT_POOL_USERS_SEARCH,
        payload: response,
      });
    })
    .catch((error: Error) => {
      dispatch({
        type: TALENT_POOL_USERS_SEARCH_ERROR,
        payload: error,
      });
    });
});

const polls = new Map();
export const pollTalentPoolStatus =
  (
    poolId: number,
    poolName?: string | null,
    isFolder?: boolean,
  ): ThunkAction<TalentPoolFolder> =>
  (dispatch: Dispatch): Promise<TalentPoolFolder> => {
    const INITIAL_TIMEOUT = 2000;
    const MAX_TIMEOUT = 10000;

    const clearExistingPoll = () => {
      const currentTimeoutId = polls.get(poolId);
      if (currentTimeoutId) {
        clearTimeout(currentTimeoutId);
      }
    };

    const handleActiveStatus = async () => {
      if (isFolder) {
        await dispatch(getFolderDetails(poolId));
        toastApi.show(
          <Toast initialFocus={0} semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>{`"${poolName ?? ''}" folder is ready`}</ToastTitle>
          </Toast>,
        );
      } else {
        await dispatch(getTalentPoolDetails(poolId));
        toastApi.show(
          <Toast initialFocus={0} semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>{`"${poolName ?? ''}" pool is ready`}</ToastTitle>
          </Toast>,
        );
      }
    };

    const checkPoolStatus = async (
      timeout: number,
      resolve: (task: TalentPoolFolder) => void,
      reject: (error: Error) => void,
    ) => {
      clearExistingPoll();

      try {
        const task = camel(
          await dispatch(
            reduxApi.get(`talent-pool-folders/${poolId}/status`, {}, API_PATH),
          ),
        );

        if (task.status === 'IN_PROGRESS') {
          const nextTimeout = Math.min(timeout * 2, MAX_TIMEOUT);
          polls.set(
            poolId,
            setTimeout(
              () => checkPoolStatus(nextTimeout, resolve, reject),
              timeout,
            ),
          );
          return;
        }

        if (task.status === 'ACTIVE') {
          handleActiveStatus();
        }

        resolve(task);
        polls.delete(poolId);
      } catch (error: any) {
        reject(error);
      }
    };

    return new Promise(
      (
        resolve: (result: Promise<TalentPoolFolder> | TalentPoolFolder) => void,
        reject: (error?: any) => void,
      ) => {
        checkPoolStatus(INITIAL_TIMEOUT, resolve, reject);
      },
    );
  };

export const createTalentPool: (
  payload: TalentPoolCreatePayload,
) => ThunkAction<number | null | undefined> = flow(
  key(() => 'talent-pool-folders'),
  fetching(),
)((payload) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post('talent-pool-folders', payload, {}, API_POST_PATH),
    // @ts-expect-error - TS2345 - Argument of type '(response: TalentPoolFolder) => number | undefined' is not assignable to parameter of type '(value: unknown) => number | PromiseLike<number | undefined> | undefined'. | TS7030 - Not all code paths return a value.
  ).then((response: TalentPoolFolder) => {
    if (response) {
      return response.id;
    }
  });
});

export const getTalentPoolDetails: (talentPoolId: number) => ThunkAction<void> =
  flow(
    key((talentPoolId: number) => `talent-pool-folders/${talentPoolId}`),
    fetching(),
  )((talentPoolId: number) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.get<TalentPoolFolder>(
        `talent-pool-folders/${talentPoolId}`,
        {},
        API_PATH,
      ),
    )
      .then((response: TalentPoolFolder) => {
        dispatch({
          type: TALENT_POOL_DETAILS,
          payload: response,
        });
      })
      .catch((error: Error) => {
        dispatch({
          type: TALENT_POOL_DETAILS_ERROR,
          payload: error,
        });
      });
  });

export const getFolderDetails: (folderId: number) => ThunkAction<void> = flow(
  key((folderId: number) => `talent-pool-folders/${folderId}`),
  fetching(),
)((folderId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get<TalentPoolFolder>(
      `talent-pool-folders/${folderId}`,
      {},
      API_PATH,
    ),
  )
    .then((response: TalentPoolFolder) => {
      dispatch({
        type: TALENT_POOL_FOLDER_DETAILS,
        payload: response,
      });
    })
    .catch((error: Error) => {
      dispatch({
        type: TALENT_POOL_FOLDER_DETAILS_ERROR,
        payload: error,
      });
    });
});

const defaultPoolFolderPayload = {
  page: 0,
  page_size: TALENT_POOL_FOLDERS_PAGE_SIZE,
} as const;

export const getTalentPoolFolders: (
  talentPoolId: number,
  payload?: {
    page: number;
    page_size: number;
  },
) => ThunkAction<void> = flow(
  key((talentPoolId: number) => `talent-pool-folders/${talentPoolId}/folders`),
  fetching(),
)(
  (talentPoolId: number, payload = defaultPoolFolderPayload) =>
    (dispatch: Dispatch) => {
      return dispatch(
        reduxApi.get<TalentPoolList>(
          `talent-pool-folders/${talentPoolId}/folders?page_size=${payload.page_size}&page=${payload.page}`,
          {},
          API_PATH,
        ),
      )
        .then((response: TalentPoolList) => {
          dispatch({
            type: TALENT_POOL_FOLDERS,
            payload: response,
          });
        })
        .catch((error: Error) => {
          dispatch({
            type: TALENT_POOL_FOLDERS_ERROR,
            payload: error,
          });
        });
    },
);

export const updateTalentPool: (
  talentPoolId: number,
  payload: TalentPoolCreatePayload,
) => ThunkAction<number | null | undefined> = flow(
  key((talentPoolId: number) => `talent-pool-folders/${talentPoolId}`),
  fetching(),
)((talentPoolId, payload) => (dispatch: Dispatch, getState: GetState) => {
  const talentPoolFolders = getState().talentPools.talentPoolFolders;
  return dispatch(
    reduxApi.patch(
      `talent-pool-folders/${talentPoolId}`,
      payload,
      {},
      API_POST_PATH,
    ),
  ).then((response) => {
    // @ts-expect-error - TS18046 - 'response' is of type 'unknown'.
    if (response.parent_id) {
      const updatedTalentPoolFolders =
        // @ts-expect-error - TS7006 - Parameter 'folder' implicitly has an 'any' type.
        talentPoolFolders.talent_pool_folders.map((folder) => {
          // @ts-expect-error - TS18046 - 'response' is of type 'unknown'.
          if (folder.id === response.id) {
            return response;
          }
          return folder;
        });
      return dispatch({
        // @ts-expect-error - TS2769 - No overload matches this call.
        type: TALENT_POOL_FOLDERS,
        payload: {
          ...talentPoolFolders,
          //@ts-expect-error
          talent_pool_folders: updatedTalentPoolFolders,
        },
      });
    }
    // @ts-expect-error - TS18046 - 'response' is of type 'unknown'.
    return response.id;
  });
});

// @ts-expect-error - TS2322 - Type '(talentPoolId: number, archive: boolean) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(talentPoolId: number, archive: boolean) => ThunkAction<void>'.
export const archiveOrUnarchiveTalentPool: (
  talentPoolId: number,
  archive: boolean,
) => ThunkAction<void> = flow(
  key(
    (talentPoolId: number, archive: boolean) =>
      `talent-pool-folders/${talentPoolId}/${
        archive ? 'archive' : 'unarchive'
      }`,
  ),
  fetching(),
)((talentPoolId, archive) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `talent-pool-folders/${talentPoolId}/${
        archive ? 'archive' : 'unarchive'
      }`,
      {},
      {},
      API_POST_PATH,
    ),
  );
});

export const fetchMultiplePoolFolders: (
  searchQuery?: string,
  focusEntity?: string,
) => ThunkAction<void> = flow(
  key(() => `talent-pool-folders/hierarchy`),
  fetching(),
)((searchQuery, focusEntity) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(
      `talent-pool-folders/hierarchy?page_size=${TALENT_POOL_FOLDERS_HIERARCHY_PAGE_SIZE}&page=0&q=${
        searchQuery ?? ''
      }&focus_entity=${focusEntity ?? ''}`,
      {},
      API_PATH,
    ),
  )
    .then((response) => {
      return dispatch({
        // @ts-expect-error - TS2769 - No overload matches this call.
        type: TALENT_POOL_FOLDERS_HIERARCHY,
        // @ts-expect-error - TS2769 - No overload matches this call.
        payload: response,
      });
    })
    .catch((error) => {
      return dispatch({
        type: TALENT_POOL_FOLDERS_HIERARCHY_ERROR,
        payload: error,
      });
    });
});

export const fetchWorkflowFolders: (filterIds: number[]) => ThunkAction<void> =
  flow(
    key(
      (filterIds: number[]) =>
        `talent-pool-folders/workflow-folders/${filterIds.join(',')}`,
    ),
    fetching(),
  )((filterIds) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.get(
        `talent-pool-folders/bulk/workflow-folders-and-pool?page_size=30&page=0&filter_ids=${filterIds.join(
          ',',
        )}`,
        {},
        API_PATH,
      ),
    )
      .then((response) => {
        return dispatch({
          // @ts-expect-error - TS2769 - No overload matches this call.
          type: TALENT_POOL_FOLDERS_WORKFLOW,
          // @ts-expect-error - TS2769 - No overload matches this call.
          payload: response,
        });
      })
      .catch((error) => {
        return dispatch({
          type: TALENT_POOL_FOLDERS_WORKFLOW_ERROR,
          payload: error,
        });
      });
  });
